/**
 * React Router paths and `?query` conventions — keep in sync with `App.tsx` `<Routes>`.
 */

export const ROUTES = {
  home: "/",
  renderer: "/renderer",
  rendererWrapper: "/renderer-wrapper",
  dev: "/dev",
  login: "/login",
  register: "/register",
  playground: "/playground",
  extensions: "/extensions",
  download: "/download",
  downloadWeb: "/download-web",
  molarMass: "/molar-mass",
  fileUpload: "/file-upload",
  shop: "/shop",
  shopProduct: "/shop/p/:slug",
  cart: "/shop/cart",
  shopOrders: "/shop/orders",
  shopOrderDetail: "/shop/orders/:id",
  shopOrderSuccess: "/shop/orders/success",
  adminShop: "/admin/shop",
  membership: "/membership",
  credits: "/credits",
  membershipElectron: "/membership-electron",
  selfIonization: "/self-ionization",
  quantumOrbitals: "/quantum-orbitals",
  blog: "/blog",
  article: "/article",
  privacy: "/privacy",
  cookies: "/cookies",
  license: "/license",
  terms: "/terms",
  support: "/support",
  balance: "/balance",
  docs: "/docs",
  /** Developer API: keys, usage, plans (same page; /api-pricing opens on plans). */
  apiKeys: "/account/api-keys",
  apiPricing: "/api-pricing",
  periodicTable: "/periodic-table",
  compare: "/compare",
  reaction: "/reaction",
  stoichiometry: "/stoichiometry",
  acidBase: "/acid-base",
  quiz: "/quiz",
  configBuilder: "/config-builder",
  workbench: "/workbench",
  /** Index of the calculators (design/nav.ts "Tools" group). */
  tools: "/tools",
  pricing: "/pricing",
  molecules: "/molecules",
  molecule: "/molecule/:formula",
  density: "/density",
  densityResult: "/density/:inchikey",
} as const;

export type AppRoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** Query keys used across Table, Renderer, App shell, Playground, Footer. */
export const APP_QUERY = {
  fullscreen: "fullscreen",
  keyword: "keyword",
  q: "q",
  searchbar: "searchbar",
  embed: "embed",
  /** Workbench → Renderer hand-off: GPAW task id to poll instead of /api/load. */
  task: "task",
  smiles: "smiles",
  label: "label",
} as const;

export type AppQueryFlags = {
  fullscreen: boolean;
  searchbar: boolean;
  presentation: boolean;
  /** Hosted in someone else's page: drop the summary card above the model. */
  embed: boolean;
  q: string | null;
  keyword: string | null;
  /** Pending GPAW density job (from the Workbench). */
  task: string | null;
  smiles: string | null;
  label: string | null;
};

export function parseAppQuery(search: string | URLSearchParams): AppQueryFlags {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  return {
    fullscreen: params.get(APP_QUERY.fullscreen) === "true",
    searchbar: params.get(APP_QUERY.searchbar) === "true",
    presentation: params.get("presentation") === "true",
    embed: params.get(APP_QUERY.embed) === "true",
    q: params.get(APP_QUERY.q),
    keyword: params.get(APP_QUERY.keyword),
    task: params.get(APP_QUERY.task),
    smiles: params.get(APP_QUERY.smiles),
    label: params.get(APP_QUERY.label),
  };
}

/** `/workbench?smiles=...` shareable link. */
export function buildWorkbenchUrl(smiles: string): string {
  const p = new URLSearchParams();
  p.set(APP_QUERY.smiles, smiles);
  return `${ROUTES.workbench}?${p.toString()}`;
}

/** `/renderer?keyword=WB_...&task=...&smiles=...&label=...` — opens a Workbench
 *  molecule in the density viewer, polling the already-queued GPAW job. */
export function buildRendererDensityUrl(key: string, taskId: string, smiles: string, label?: string): string {
  const p = new URLSearchParams();
  p.set(APP_QUERY.keyword, key);
  p.set(APP_QUERY.task, taskId);
  p.set(APP_QUERY.smiles, smiles);
  if (label) p.set(APP_QUERY.label, label);
  return `${ROUTES.renderer}?${p.toString()}`;
}

/** `/?searchbar=true&q=...` */
export function buildHomeSearchUrl(searchText: string): string {
  const p = new URLSearchParams();
  p.set(APP_QUERY.searchbar, "true");
  p.set(APP_QUERY.q, searchText);
  return `${ROUTES.home}?${p.toString()}`;
}

/** `/renderer?fullscreen=true&keyword=...` */
export function buildRendererSearch(
  options?: { fullscreen?: boolean; keyword?: string }
): string {
  const p = new URLSearchParams();
  if (options?.fullscreen) p.set(APP_QUERY.fullscreen, "true");
  if (options?.keyword) p.set(APP_QUERY.keyword, options.keyword);
  const qs = p.toString();
  return qs ? `${ROUTES.renderer}?${qs}` : ROUTES.renderer;
}
